import ChannelContext from '@jetshop/core/components/ChannelContext';
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import Accordion from '@jetshop/ui/Accordion/Accordion';
import { useQuery } from 'react-apollo';
import AcademyQuery from './AcademyQuery.gql';
import styled from 'react-emotion';
import theme from '@jetshop/ui/utils/theme';
import { ReactComponent as SkiStoreLogo } from '../../svg/Logo.svg';
import { ReactComponent as MtbStoreLogo } from '../../svg/MtbStoreWhite.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const Sidebar = styled('aside')`
  background: ${theme('colors.background')};
  a {
    color: #000;
    text-decoration: none;
    transition: all ease 0.3s;
    &:hover {
      color: ${theme('colors.accent')};
      transition: all ease 0.3s;
    }
    &.active {
      font-weight: bold;
    }
  }
  ul {
    padding: 0 20px;
  }
`;

const LogoWrapper = styled('div')`
  text-align: center;
  padding: 3rem;

  svg {
    width: 150px;
  }

  path.cls-2 {
    fill: black;
  }

  ${theme('below.lg')} {
    padding: 1.5rem;
  }
  ${theme('below.md')} {
    height: 35%;
    margin: auto;
    margin-bottom: 0.5rem;
    padding: 1rem;
    text-align: center;
    width: 35%;
  }
  ${theme('below.xs')} {
    padding: 0;
    margin-top: 0.25rem;
    margin-bottom: 1rem;
  }
`;

const MainCat = styled('div')`
  li {
    border-top: 1px solid ${theme('colors.lightgrey')};
    display: flex;
    align-content: center;
    justify-content: space-between;
    font-weight: bold;
    padding: 0.5rem 0;
    text-transform: uppercase;
    font-size: 1rem;
    font-family: ${theme('fonts.secondary')};
    transition: all, 0.2s ease;

    &:hover,
    &.active {
      color: ${theme('colors.primary')};
    }
  }
`;

const SubCats = styled('li')`
  :last-of-type {
    margin-bottom: 0.75rem;
  }

  font-size: 14px;
  a {
    transition: all, 0.2s ease;
    &.active {
      font-weight: bold;
      color: ${theme('colors.primary')};
    }
  }

  &:hover a {
    color: ${theme('colors.primary')};
  }
`;

const IndicatorIcon = styled('span')`
  margin-left: auto;
  font-size: 1rem;
  padding-right: 1px;
`;

const Title = styled('h1')`
  font-size: 1.5rem;
  padding: 0 20px;
`;

const AccordionIndicator = ({ isOpen }) => (
  <IndicatorIcon>
    {isOpen ? (
      <FontAwesomeIcon icon={['fal', 'angle-down']} />
    ) : (
      <FontAwesomeIcon icon={['fal', 'angle-right']} />
    )}
  </IndicatorIcon>
);

const AcademySidebar = ({ path, pathname }) => {
  const { selectedChannel } = useContext(ChannelContext);
  const { data, loading, error } = useQuery(AcademyQuery, {
    variables: {
      pathname: path
    }
  });

  if (error) return null;
  if (loading) return null;

  const {
    route: { object }
  } = data;

  return (
    <Sidebar>
      <LogoWrapper>
        <Link to={object.primaryRoute.path}>
          {selectedChannel.id === 1 ? <SkiStoreLogo /> : <MtbStoreLogo />}
        </Link>
      </LogoWrapper>
      <Accordion single>
        {({ openIndexes, handleClick, AccordionContent }) => (
          <>
            <Title>
              <Link to={object.primaryRoute.path}>{object.name}</Link>
            </Title>
            {object.subcategories.map((category, index) => (
              <div key={index}>
                <ul>
                  <Link
                    to={category.primaryRoute.path}
                    onClick={() => {
                      handleClick(index);
                    }}
                  >
                    <MainCat>
                      <li
                        className={
                          openIndexes.includes(index) ||
                          pathname.indexOf(category.primaryRoute.path) > -1
                            ? 'active'
                            : 'not-active'
                        }
                      >
                        <span>{category.name}</span>
                        <AccordionIndicator
                          isOpen={
                            openIndexes.includes(index) ||
                            pathname.indexOf(category.primaryRoute.path) > -1
                          }
                        />
                      </li>
                    </MainCat>
                  </Link>
                </ul>
                {category.subcategories && category.subcategories.length > 0 && (
                  <AccordionContent
                    isOpen={
                      openIndexes.includes(index) ||
                      pathname.indexOf(category.primaryRoute.path) > -1
                    }
                  >
                    <ul>
                      {category.subcategories.map((subCat, subIndex) => (
                        <SubCats key={subIndex}>
                          <Link
                            className={
                              subCat.primaryRoute.path === pathname && 'active'
                            }
                            to={subCat.primaryRoute.path}
                          >
                            {subCat.name}
                          </Link>
                        </SubCats>
                      ))}
                    </ul>
                  </AccordionContent>
                )}
              </div>
            ))}
          </>
        )}
      </Accordion>
    </Sidebar>
  );
};

export default AcademySidebar;
